import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { OwnerNav } from "@/components/Layout";
import { api, formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Check, CreditCard, Loader2, ShieldAlert, ShieldCheck } from "lucide-react";

const STATUS_META = {
  none: { label: "Aucun abonnement actif", cls: "border-[#A39E93]/40 text-[#A39E93]", icon: CreditCard },
  active: { label: "Abonnement actif", cls: "border-[#5E8C5E]/50 text-[#8FBF8F]", icon: ShieldCheck },
  past_due: { label: "Paiement en retard", cls: "border-[#C5A059]/50 text-[#C5A059]", icon: ShieldAlert },
  expired: { label: "Abonnement expiré — espace suspendu", cls: "border-[#8C3131]/50 text-[#E08D8D]", icon: ShieldAlert },
};

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }); } catch { return iso; } };
const fmtPrice = (a) => `${Number(a).toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`;
const intervalLabel = (i) => (i === "year" ? "an" : "mois");

export default function Billing() {
  const [params, setParams] = useSearchParams();
  const [sub, setSub] = useState(null);
  const [plans, setPlans] = useState([]);
  const [paying, setPaying] = useState(null);
  const [polling, setPolling] = useState(false);

  const loadSub = useCallback(async () => {
    try {
      const [s, p] = await Promise.all([api.get("/owner/subscription"), api.get("/plans")]);
      setSub(s.data);
      setPlans(p.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  // Poll payment status when returning from Stripe
  const pollStatus = useCallback(async (sessionId, attempt = 0) => {
    if (attempt >= 8) {
      setPolling(false);
      toast.error("Le statut du paiement met du temps à se confirmer. Actualisez la page dans quelques instants.");
      return;
    }
    try {
      const { data } = await api.get(`/billing/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setPolling(false);
        toast.success("Paiement confirmé ! Votre abonnement est actif. 🎉");
        params.delete("session_id"); setParams(params, { replace: true });
        loadSub();
        return;
      }
      if (data.status === "expired") {
        setPolling(false);
        toast.error("La session de paiement a expiré. Veuillez réessayer.");
        params.delete("session_id"); setParams(params, { replace: true });
        return;
      }
      setTimeout(() => pollStatus(sessionId, attempt + 1), 2000);
    } catch {
      setTimeout(() => pollStatus(sessionId, attempt + 1), 2000);
    }
  }, [params, setParams, loadSub]);

  useEffect(() => {
    loadSub();
    const sid = params.get("session_id");
    if (sid) { setPolling(true); pollStatus(sid); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const subscribe = async (planId) => {
    setPaying(planId);
    try {
      const { data } = await api.post("/billing/checkout", { plan_id: planId, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e));
      setPaying(null);
    }
  };

  const payInvoice = async () => {
    setPaying("invoice");
    try {
      const { data } = await api.get("/billing/pay-invoice");
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e));
      setPaying(null);
    }
  };

  if (!sub) return <div className="min-h-screen bg-[#0A0908] flex items-center justify-center text-[#A39E93]">Chargement…</div>;

  const meta = STATUS_META[sub.status] || STATUS_META.none;
  const StatusIcon = meta.icon;
  const payDisabled = sub.payment_configured === false;

  return (
    <div className="min-h-screen bg-[#0A0908] text-[#F4F0EA]">
      <OwnerNav />
      <Toaster theme="dark" />
      <div className="max-w-4xl mx-auto px-6 py-12 space-y-12">
        <div>
          <div className="text-[10px] tracking-[0.4em] text-[#C5A059] uppercase mb-2">Facturation</div>
          <h1 className="font-serif text-4xl sm:text-5xl font-light">Abonnement.</h1>
          <p className="text-sm text-[#A39E93] mt-3">Gérez l&apos;abonnement de votre salon et accédez à toutes les fonctionnalités.</p>
        </div>

        {polling && (
          <div className="flex items-center gap-3 p-4 bg-[#141311] border border-[#C5A059]/40 text-sm" data-testid="billing-polling">
            <Loader2 className="w-4 h-4 animate-spin text-[#C5A059]" /> Vérification de votre paiement en cours…
          </div>
        )}

        {/* Current status */}
        <section className="bg-[#141311] border border-[#2C2A26] p-6" data-testid="billing-current">
          <div className={`inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] px-3 py-1.5 border ${meta.cls}`} data-testid="billing-status">
            <StatusIcon className="w-4 h-4" strokeWidth={1.5} /> {meta.label}
          </div>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-5 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B6760] mb-1">Formule</div>
              <div className="text-[#F4F0EA]">{sub.plan ? sub.plan.name : "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B6760] mb-1">Prochaine échéance</div>
              <div className="text-[#F4F0EA]">{sub.current_period_end ? fmtDate(sub.current_period_end) : "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B6760] mb-1">Jours restants</div>
              <div className="text-[#F4F0EA]">{sub.days_remaining != null ? `${sub.days_remaining} j` : "—"}</div>
            </div>
          </div>
          {sub.status === "active" && (
            <p className="mt-4 text-sm text-[#8FBF8F]" data-testid="billing-active-note">Le prélèvement est automatique à chaque échéance. Aucune action requise de votre part.</p>
          )}
          {sub.status === "past_due" && (
            <div className="mt-4 space-y-3" data-testid="billing-pastdue-note">
              <p className="text-sm text-[#C5A059]">Le dernier prélèvement a échoué. Réglez par carte pour éviter la suspension de votre site et de votre espace.</p>
              <button onClick={payInvoice} disabled={paying === "invoice"} className="btn-primary disabled:opacity-50" data-testid="billing-pay-invoice">
                {paying === "invoice" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" strokeWidth={1.5} />}
                {paying === "invoice" ? "Redirection…" : "Régler par carte"}
              </button>
            </div>
          )}
          {sub.status === "expired" && (
            <p className="mt-4 text-sm text-[#E08D8D]" data-testid="billing-expired-note">Votre abonnement a expiré et votre espace est suspendu. Choisissez une formule ci-dessous pour le réactiver immédiatement.</p>
          )}
        </section>

        {/* Plans — shown to subscribe (no active subscription) */}
        {sub.status !== "active" && sub.status !== "past_due" && (
        <section>
          <h2 className="font-serif text-2xl font-light mb-6">Choisissez votre formule</h2>
          {payDisabled && (
            <div className="mb-6 p-4 bg-[#141311] border border-[#C5A059]/40 text-sm text-[#C5A059]" data-testid="billing-not-configured">
              Le paiement en ligne n&apos;est pas encore activé. Contactez votre administrateur pour finaliser la mise en place.
            </div>
          )}
          {plans.length === 0 ? (
            <div className="border border-dashed border-[#2C2A26] p-8 text-center text-[#6B6760]">Aucune formule disponible pour le moment. Contactez votre administrateur.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="billing-plans">
              {plans.map((p) => (
                <div key={p.id} className="bg-[#141311] border border-[#2C2A26] p-6 flex flex-col hover:border-[#C5A059]/50 transition-colors" data-testid={`billing-plan-${p.id}`}>
                  <div className="font-serif text-2xl font-light">{p.name}</div>
                  {p.description && <p className="text-sm text-[#A39E93] mt-2">{p.description}</p>}
                  <div className="mt-4 flex items-baseline gap-1">
                    <span className="font-display text-4xl text-[#C5A059]">{fmtPrice(p.amount)}</span>
                    <span className="text-sm text-[#6B6760]">/ {intervalLabel(p.interval)}</span>
                  </div>
                  {p.features?.length > 0 && (
                    <ul className="mt-5 space-y-2 flex-1">
                      {p.features.map((f, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-[#A39E93]">
                          <Check className="w-4 h-4 text-[#C5A059] mt-0.5 shrink-0" strokeWidth={2} /> {f}
                        </li>
                      ))}
                    </ul>
                  )}
                  <button onClick={() => subscribe(p.id)} disabled={paying === p.id || payDisabled} className="btn-primary w-full justify-center mt-6 disabled:opacity-50" data-testid={`billing-pay-${p.id}`}>
                    {paying === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" strokeWidth={1.5} />}
                    {paying === p.id ? "Redirection…" : (sub.status === "active" ? "Renouveler" : "S'abonner")}
                  </button>
                </div>
              ))}
            </div>
          )}
          <p className="text-xs text-[#6B6760] mt-6">Paiement sécurisé par Stripe. Le prélèvement sera automatique à chaque échéance ; vous pourrez résilier à tout moment.</p>
        </section>
        )}
      </div>
    </div>
  );
}
